import { Button } from "primereact/button";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { TimesCircleIcon } from "primereact/icons/timescircle";
import { Tag } from "primereact/tag";

import { ListEmptyState, Loading, SelectableCard } from "@/ds";

import {
  BUSCAR_PROFISSIONAL_PLACEHOLDER,
  CONFLITO_DESCRICAO_CURTA,
  DISPONIVEL_LABEL,
  INDISPONIVEL_LABEL,
  OBRA_SECTION_LABEL,
  PERIODO_ALOCADO_LABEL,
  PERIODO_OBRA_LABEL,
  PROFISSIONAIS_EMPTY_MESSAGE,
  PROFISSIONAIS_SEARCH_EMPTY,
  SUBSTITUTO_SECTION_LABEL,
} from "../../resolver-conflito.constants";
import styles from "./substituto-step.module.scss";
import type { SubstitutoStepViewModel } from "./types-substituto-step";

export function SubstitutoStepView({
  profissionalNome,
  profissionalTime,
  obras,
  errors,
  search,
  substitutos,
  escolherObra,
  escolherSubstituto,
  buscar,
  limparBusca,
}: SubstitutoStepViewModel) {
  return (
    <>
      <div className={styles.resumo} data-testid="resolver-conflito-resumo-curto">
        <span>
          <strong>{profissionalNome}</strong>
          {profissionalTime ? ` • ${profissionalTime}` : null}
        </span>
        <span className={styles.resumoInfo}>
          <i className="pi pi-info-circle" aria-hidden />
          {CONFLITO_DESCRICAO_CURTA}
        </span>
      </div>

      <fieldset className={styles.secao} aria-describedby="resolver-conflito-obra-error">
        <legend className={styles.secaoTitulo}>{OBRA_SECTION_LABEL}</legend>
        <div className={styles.opcoes}>
          {obras.map((obra) => (
            <SelectableCard
              key={obra.id}
              value={obra.id}
              selected={obra.marcada}
              name="resolver-conflito-obra"
              onSelect={escolherObra}
              className={errors.obra ? styles.invalida : undefined}
              data-testid="resolver-conflito-obra-option"
            >
              <span className={styles.opcaoCorpo}>
                <span className={styles.opcaoTitulo}>{obra.titulo}</span>
                <span className={styles.opcaoMeta}>
                  <i className="pi pi-briefcase" aria-hidden />
                  {PERIODO_OBRA_LABEL}: {obra.periodoObra}
                </span>
                <span className={styles.opcaoMeta}>
                  <i className="pi pi-user" aria-hidden />
                  {PERIODO_ALOCADO_LABEL}: {obra.periodoAlocado}
                </span>
              </span>
            </SelectableCard>
          ))}
        </div>
        {errors.obra ? (
          <span
            id="resolver-conflito-obra-error"
            className={styles.erro}
            role="alert"
            data-testid="resolver-conflito-obra-error"
          >
            {errors.obra}
          </span>
        ) : null}
      </fieldset>

      <fieldset className={styles.secao} aria-describedby="resolver-conflito-substituto-error">
        <legend className={styles.secaoTitulo}>{SUBSTITUTO_SECTION_LABEL}</legend>
        <IconField iconPosition="left" className={styles.busca}>
          <InputIcon className="pi pi-search" />
          <InputText
            className={styles.buscaCampo}
            value={search}
            placeholder={BUSCAR_PROFISSIONAL_PLACEHOLDER}
            aria-label={BUSCAR_PROFISSIONAL_PLACEHOLDER}
            maxLength={100}
            onChange={(event) => buscar(event.target.value)}
            data-testid="resolver-conflito-busca"
          />
        </IconField>

        {substitutos.tipo === "carregando" ? (
          <div className={styles.estado} data-testid="resolver-conflito-substitutos-loading">
            <span>
              <Loading size="sm" aria-hidden /> Carregando profissionais...
            </span>
          </div>
        ) : null}

        {substitutos.tipo === "erro" ? (
          <div className={styles.falha}>
            <div
              className={styles.falhaMensagem}
              role="alert"
              data-testid="resolver-conflito-substitutos-load-error"
            >
              <TimesCircleIcon className={styles.falhaIcone} />
              <span className={styles.falhaTexto}>{substitutos.mensagem}</span>
            </div>
            {substitutos.recarregar ? (
              <Button
                type="button"
                label="Tentar novamente"
                severity="secondary"
                outlined
                onClick={substitutos.recarregar}
                data-testid="resolver-conflito-substitutos-retry"
              />
            ) : null}
          </div>
        ) : null}

        {substitutos.tipo === "semResultado" ? (
          <ListEmptyState
            noun="profissional"
            gender="m"
            title={PROFISSIONAIS_SEARCH_EMPTY.title}
            description={PROFISSIONAIS_SEARCH_EMPTY.description}
            hasSearch
            hasFilters={false}
            onClear={limparBusca}
            testId="resolver-conflito-substitutos-empty"
            clearTestId="resolver-conflito-substitutos-empty-clear"
            data-variant="search"
          />
        ) : null}

        {substitutos.tipo === "vazio" ? (
          <div className={styles.estado} data-testid="resolver-conflito-substitutos-empty">
            {PROFISSIONAIS_EMPTY_MESSAGE}
          </div>
        ) : null}

        {substitutos.tipo === "lista" ? (
          <ul
            className={`${styles.lista} ${errors.substituto ? styles.invalida : ""}`}
            data-testid="resolver-conflito-substitutos"
          >
            {substitutos.opcoes.map((item) => (
              <li key={item.id}>
                <SelectableCard
                  value={item.id}
                  selected={item.marcado}
                  name="resolver-conflito-substituto"
                  onSelect={escolherSubstituto}
                  className={styles.item}
                  data-testid="resolver-conflito-substituto-option"
                >
                  <span className={styles.itemMeta}>
                    <span className={styles.itemNome}>{item.nome}</span>
                    <span className={styles.itemPapel}>{item.papel}</span>
                  </span>
                  <Tag
                    className="p-tag-xs"
                    severity={item.indisponivel ? "warning" : "success"}
                    value={item.indisponivel ? INDISPONIVEL_LABEL : DISPONIVEL_LABEL}
                  />
                </SelectableCard>
              </li>
            ))}
          </ul>
        ) : null}

        {errors.substituto ? (
          <span
            id="resolver-conflito-substituto-error"
            className={styles.erro}
            role="alert"
            data-testid="resolver-conflito-substituto-error"
          >
            {errors.substituto}
          </span>
        ) : null}
      </fieldset>
    </>
  );
}
